import React from 'react';
import styled from '@emotion/styled';
import {
	Gutter,
	ITheme,
	RowGroupRole,
	RowRole,
	TableRole,
	VisuallyHidden,
} from '../../../../../ui';
import { Item } from './item';
import { IEnrichedFlight } from '../../../shared/flight/domain/models/IEnrichedFlight';
import { useTranslation } from '../../../../shared/i18n/translate/useTranslation';
import themed from '../../../../../ui/themer/themed';
import Responsive from '../../../../../ui/utils/Responsive';
import { StyledTimeColumn } from './item/Time';
import { StyledDescriptionColumn } from './item/Description';
import { StyledTypeColumn } from './item/Type';
import { StyledStatusColumn } from './item/Status';
import { StyledGateColumn } from './item/Gate';
import { StyledBaggageClaimColumn } from './item/BaggageClaim';
import { StyledDetailsLinkColumn } from './item/DetailsLink';
import { Color } from '../../../../shared/styles/Color';
import { StyledAirlineColumn } from './item/Airline';

export const StyledFlightList = styled(TableRole)<{}, ITheme>`
	margin: 5px 0;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin: 10px 0;
		border-top: ${themed(({ border }) => border.default)};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobile')}) {
		${Gutter.cssVariableName.vertical}: 9px;
	}
`;

interface IFlightListProps {
	flights: IEnrichedFlight[];
	loadedFromIndex: number;
	loadedAmount: number;
	canNavigateBackward: boolean;
}

export const FlightList = ({
	flights,
	loadedFromIndex,
	loadedAmount,
	canNavigateBackward,
}: IFlightListProps) => {
	const { t } = useTranslation();
	const isEarlier = loadedFromIndex === loadedAmount;
	const loadedFlightsMessage =
		loadedAmount > 0
			? isEarlier
				? t('showing_earlier_flights_sr', { amount: loadedAmount })
				: t('showing_later_flights_sr', { amount: loadedAmount })
			: t('no_more_flights_sr');

	return (
		<StyledFlightList>
			<StyledFlightListHeader canNavigateBackward={canNavigateBackward}>
				<StyledRowRole>
					<StyledTimeColumn>{t('flight_list_header_time')}</StyledTimeColumn>
					<StyledDescriptionColumn>
						{t('flight_list_header_description')}
					</StyledDescriptionColumn>
					<StyledTypeColumn>{t('flight_list_header_type')}</StyledTypeColumn>
					<StyledStatusColumn>
						{t('flight_list_header_status')}
					</StyledStatusColumn>
					<StyledAirlineColumn>
						{t('flight_list_header_airline')}
					</StyledAirlineColumn>
					<StyledGateColumn>{t('flight_list_header_gate')}</StyledGateColumn>
					<StyledBaggageClaimColumn>
						{t('flight_list_header_baggage_claim')}
					</StyledBaggageClaimColumn>
					<StyledDetailsLinkColumn>
						<VisuallyHidden as="span">
							{t('flight_list_details_link')}
						</VisuallyHidden>
					</StyledDetailsLinkColumn>
				</StyledRowRole>
			</StyledFlightListHeader>

			<StyledRowGroupRole>
				{flights.map((flight, index) => (
					<React.Fragment key={flight.id}>
						{loadedFromIndex === index && (
							<VisuallyHidden as="div">{loadedFlightsMessage}</VisuallyHidden>
						)}
						<Item flight={flight} />
					</React.Fragment>
				))}
			</StyledRowGroupRole>

			<VisuallyHidden as="div" aria-live="polite" role="status">
				{loadedFlightsMessage}
			</VisuallyHidden>
		</StyledFlightList>
	);
};

const StyledFlightListHeader = styled(RowGroupRole)<
	{ canNavigateBackward: boolean },
	ITheme
>`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	padding: 15px 0;
	border-bottom: ${themed(({ border }) => border.default)};
	font-size: 16px;
	color: ${Color.Background2};
	margin-bottom: ${({ canNavigateBackward }) =>
		canNavigateBackward ? '60px' : '0'};

	&:last-child {
		border-bottom: none;
	}

	${StyledGateColumn} {
		font-weight: 300;
		color: ${Color.Background2};
	}

	${StyledBaggageClaimColumn} {
		font-weight: 300;
		color: ${Color.Background2};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: 16px;

		&:last-child {
			border-bottom: ${themed(({ border }) => border.default)};
		}
	}
`;

const StyledRowRole = styled(RowRole)`
	display: contents;
`;

const StyledRowGroupRole = styled(RowGroupRole)`
	display: contents;
`;
